import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { API_BASE_URL } from '../config';
import '../styles/AuthPages.css';

const GuestQRLogin = ({ onLogin }) => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [autoLoginAttempted, setAutoLoginAttempted] = useState(false);

  // Check for QR code data in URL or localStorage
  useEffect(() => {
    // Check URL params (from QR scan)
    const qrData = searchParams.get('data');
    if (qrData && !autoLoginAttempted) {
      try {
        const parsed = JSON.parse(decodeURIComponent(qrData));
        if (parsed.username && parsed.password) {
          setUsername(parsed.username);
          setPassword(parsed.password);
          // Auto-login after a brief moment
          setTimeout(() => {
            handleLogin(parsed.username, parsed.password);
          }, 500);
          setAutoLoginAttempted(true);
        }
      } catch (err) {
        console.error('Error parsing QR data:', err);
      }
    }
  }, [searchParams, autoLoginAttempted]);

  const handleLogin = async (user = username, pass = password) => {
    if (!user || !pass) {
      setError('Please enter username and password');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await axios.post(`${API_BASE_URL}/api/login/credentials`, {
        username: user,
        password: pass
      });

      if (response.data.userId && response.data.token) {
        // Call parent onLogin handler
        onLogin(response.data.userId, response.data.token);

        // Navigate to market
        navigate('/market');
      } else {
        setError('Login failed. Please try again.');
      }
    } catch (err) {
      console.error('Login error:', err);
      setError(err.response?.data?.message || 'Invalid username or password');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    handleLogin();
  };

  return (
    <div className="auth-container">
      <div className="auth-card">
        <div className="auth-header">
          <h1>🎉 STOCK MARKET PARTY</h1>
          <h2>Guest Login</h2>
          {autoLoginAttempted && (
            <p className="auto-login-note">✨ Logging you in from badge...</p>
          )}
        </div>

        {error && (
          <div className="error-banner">
            ⚠️ {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="auth-form">
          <div className="form-group">
            <label htmlFor="username">Username</label>
            <input
              id="username"
              type="text"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              placeholder="Enter your username"
              disabled={loading}
              autoComplete="username"
            />
          </div>

          <div className="form-group">
            <label htmlFor="password">Password</label>
            <input
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Enter your password"
              disabled={loading}
              autoComplete="current-password"
            />
          </div>

          <button
            type="submit"
            className="auth-button"
            disabled={loading || !username || !password}
          >
            {loading ? '⏳ Logging in...' : '🚀 Start Trading'}
          </button>
        </form>

        <div className="auth-footer">
          <p className="hint">
            📱 Scan your badge QR code to login automatically
          </p>
          <p className="hint">
            ❓ Don't have a badge? See the registration desk
          </p>
        </div>
      </div>
    </div>
  );
};

export default GuestQRLogin;
